import { useEffect } from 'react';
import { useCoreClient } from '@hooks';
import {
  campaignsState,
  loadingState,
  notificationErrorState,
  prePurchasePreviewLinksState,
  selectedCampaignGidState,
} from '@states';
import { impersonateAccessTokenState } from 'src/states/impersonate';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Campaign } from '@givz/core-client';
import { useIsImpersonator } from '@hooks/auth';

export const useFetchCampaigns = () => {
  const isImpersonator = useIsImpersonator();
  const [, setLoading] = useRecoilState(loadingState);
  const [campaigns, setCampaigns] = useRecoilState(campaignsState);
  const [, setNotificationError] = useRecoilState(notificationErrorState);
  const impersonateAccountAccessToken = useRecoilValue(
    impersonateAccessTokenState,
  );
  const coreClient = useCoreClient();

  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoading(true);

      const { data, errors } = await coreClient().Campaigns.getAll();

      if (errors) {
        setNotificationError(errors);
      } else {
        setCampaigns(data.campaigns);
      }

      setLoading(false);
    };

    if (isImpersonator) {
      if (impersonateAccountAccessToken) {
        fetchCampaigns();
      }
      return;
    }

    fetchCampaigns();
  }, [
    isImpersonator,
    impersonateAccountAccessToken,
    setLoading,
    setNotificationError,
    setCampaigns,
    coreClient,
  ]);

  const updateSelectedCampaign = (selectedCampaign: Campaign) => {
    setCampaigns((campaigns) => {
      const updateIndex = campaigns.findIndex(
        (campaign) => campaign.gid === selectedCampaign.gid,
      );
      return campaigns.map((campaign, index) =>
        index === updateIndex ? selectedCampaign : campaign,
      );
    });
  };

  return { campaigns, setCampaigns, updateSelectedCampaign };
};

export const useFetchPrePurchasePromotionPreviewLinks = () => {
  const [, setLoading] = useRecoilState(loadingState);
  const selectedCampaignGid = useRecoilValue(selectedCampaignGidState);
  const [prePurchasePreviewLinks, setPrePurchasePreviewLinks] = useRecoilState(
    prePurchasePreviewLinksState,
  );
  const [, setNotificationError] = useRecoilState(notificationErrorState);
  const coreClient = useCoreClient();

  useEffect(() => {
    const fetchPrePurchaseLinks = async () => {
      if (selectedCampaignGid) {
        setLoading(true);

        const { data, errors } =
          await coreClient().Campaigns.getShopifyPrePurchasePromotionPreviewLinks(
            {
              gid: selectedCampaignGid,
            },
          );

        if (errors) {
          setNotificationError(errors);
        } else {
          setPrePurchasePreviewLinks(data);
        }

        setLoading(false);
      }
    };

    fetchPrePurchaseLinks();
  }, [
    coreClient,
    selectedCampaignGid,
    setLoading,
    setNotificationError,
    setPrePurchasePreviewLinks,
  ]);

  return prePurchasePreviewLinks;
};
